import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "./api";
import { clearThemeSnapshot } from "./theme-snapshot";

const AuthCtx = createContext(null);

// iter-121.40 — Soft-warning grace period (ms) before auto sign-out when the
// FE detects the tenant has been deactivated. 120s gives users enough time
// to save in-flight edits without holding a deactivated session forever.
const TENANT_DEACTIVATED_GRACE_MS = 120_000;
// Background /me poll cadence (ms). 60s strikes a balance between latency
// and avoiding traffic noise — server-side bookkeeping is cheap.
const ME_POLL_INTERVAL_MS = 60_000;

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem("analyzio_user");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(true);
    // iter-121.40 — Set the first moment the FE observes tenant_active=false.
    // The banner uses this timestamp to drive its visible countdown without
    // having to re-render the entire AuthProvider on every tick.
    const [tenantDeactivatedAt, setTenantDeactivatedAt] = useState(null);
    const pollTimerRef = useRef(null);
    const graceTimerRef = useRef(null);

    useEffect(() => {
        const path = window.location.pathname;
        if (path.startsWith("/sso/callback") || path.startsWith("/accept-invite")) {
            setLoading(false);
            return;
        }
        const tok = localStorage.getItem("analyzio_token");
        if (!tok) {
            setLoading(false);
            return;
        }
        api.get("/auth/me")
            .then((r) => {
                setUser(r.data);
                localStorage.setItem("analyzio_user", JSON.stringify(r.data));
            })
            .catch(() => {
                localStorage.removeItem("analyzio_token");
                localStorage.removeItem("analyzio_user");
                setUser(null);
            })
            .finally(() => setLoading(false));
    }, []);

    const login = async (email, password, { force = false } = {}) => {
        const r = await api.post("/auth/login", { email, password, force });
        localStorage.setItem("analyzio_token", r.data.access_token);
        localStorage.setItem("analyzio_user", JSON.stringify(r.data.user));
        setUser(r.data.user);
        setTenantDeactivatedAt(null);
        // iter-121.53 — /login response doesn't include
        // tenant_lifecycle / tenant_trial_ends_at (those are computed by
        // /me from the tenant doc). Fetch /me right away so the
        // TrialDaysChip can render without waiting for the 60s poll.
        try {
            const me = await api.get("/auth/me");
            if (me?.data) {
                setUser(me.data);
                localStorage.setItem("analyzio_user", JSON.stringify(me.data));
            }
        } catch {
            // Non-fatal — the background poll will catch up.
        }
        return r.data.user;
    };

    // iter-121 Sprint 1 — self-signup helper. Mirrors `login` but accepts
    // the full SignupRequest payload and returns `{user, tenant}` so the
    // caller can route to /onboarding with the trial context.
    const signup = async (payload) => {
        const r = await api.post("/auth/signup", payload);
        localStorage.setItem("analyzio_token", r.data.access_token);
        localStorage.setItem("analyzio_user", JSON.stringify(r.data.user));
        setUser(r.data.user);
        setTenantDeactivatedAt(null);
        // iter-121.53 — refresh /me so tenant_lifecycle + trial_ends_at
        // are immediately available for the TrialDaysChip.
        try {
            const me = await api.get("/auth/me");
            if (me?.data) {
                setUser(me.data);
                localStorage.setItem("analyzio_user", JSON.stringify(me.data));
            }
        } catch {
            // Non-fatal.
        }
        return {
            user: r.data.user,
            tenant: r.data.tenant,
            // Sprint 2 — demo OTP code surfaced when SMTP is off.
            email_verify_demo_code: r.data.email_verify_demo_code || null,
        };
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        localStorage.removeItem("analyzio_token");
        localStorage.removeItem("analyzio_user");
        // Approach A — drop the theme snapshot so the post-logout reload's
        // <head> bootstrap doesn't flash the previous user's theme on /login.
        clearThemeSnapshot();
        setUser(null);
        setTenantDeactivatedAt(null);
        window.location.href = "/login";
    };

    const refreshMe = async () => {
        try {
            const r = await api.get("/auth/me");
            setUser(r.data);
            localStorage.setItem("analyzio_user", JSON.stringify(r.data));
            // iter-121.40 — trip the deactivation banner the moment /me
            // surfaces tenant_active=false. We only set the timestamp ONCE
            // so the countdown stays monotonic even if the poll fires again.
            if (r.data?.tenant_active === false) {
                setTenantDeactivatedAt((curr) => curr ?? Date.now());
            }
            return r.data;
        } catch {
            return null;
        }
    };

    // iter-121.40 — Background poll. Runs only when authenticated, pauses
    // when the document is hidden so background tabs don't burn requests.
    useEffect(() => {
        if (!user) {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            return;
        }
        const tick = () => {
            if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
            refreshMe();
        };
        pollTimerRef.current = setInterval(tick, ME_POLL_INTERVAL_MS);
        // Also refresh immediately when the tab regains focus so a returning
        // user discovers a remote deactivation faster than the next poll.
        const onVisible = () => {
            if (document.visibilityState === "visible") refreshMe();
        };
        document.addEventListener("visibilitychange", onVisible);
        // iter-2026 — When any protected route reports the trial has expired
        // (403 trial_expired, broadcast by the api interceptor), refresh /me
        // right away so the global TrialBanner overlay renders without waiting
        // for the next background poll.
        const onTrialExpired = () => { refreshMe(); };
        window.addEventListener("trial-expired", onTrialExpired);
        return () => {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("trial-expired", onTrialExpired);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    // iter-121.40 — Once the FE observes tenant_active=false, start a 120s
    // grace timer then auto sign-out. The TenantDeactivationBanner reads
    // `tenantDeactivatedAt` + the grace window to render its countdown.
    useEffect(() => {
        if (!tenantDeactivatedAt) return;
        graceTimerRef.current = setTimeout(() => { logout(); }, TENANT_DEACTIVATED_GRACE_MS);
        return () => { if (graceTimerRef.current) clearTimeout(graceTimerRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantDeactivatedAt]);

    return (
        <AuthCtx.Provider value={{
            user, setUser, loading, login, signup, logout, refreshMe,
            tenantDeactivatedAt,
            tenantDeactivatedGraceMs: TENANT_DEACTIVATED_GRACE_MS,
        }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
